(() => {
  const selector = '[data-category-pills]';
  const instances = new WeakMap();

  class CategoryPills {
    constructor(section) {
      if (instances.has(section)) return;
      this.section = section;
      this.viewport = section.querySelector('[data-category-pills-viewport]');
      this.slider = section.querySelector('[data-category-pills-slider]');
      this.track = section.querySelector('[data-category-pills-track]');
      this.previous = section.querySelector('[data-category-pills-prev]');
      this.next = section.querySelector('[data-category-pills-next]');
      this.media = matchMedia('(min-width: 768px)');
      this.abort = new AbortController();
      this.frame = 0;
      if (!this.viewport || !this.slider || !this.track) return;
      this.bind();
      this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
      this.resizeObserver.observe(this.slider);
      this.resizeObserver.observe(this.track);
      this.update();
      instances.set(section, this);
    }

    bind() {
      const { signal } = this.abort;
      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.slider.addEventListener('scroll', () => this.queueUpdate(), { signal, passive: true });
      this.section.addEventListener('focusin', event => {
        if (!this.media.matches) return;
        const slide = event.target.closest('[data-category-pill-slide]');
        slide?.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }, { signal });
      this.media.addEventListener?.('change', () => this.syncMode(), { signal });
    }

    prefersReducedMotion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    move(direction) {
      const distance = Math.max(240, this.slider.clientWidth * .75) * direction;
      this.slider.scrollBy({ left: distance, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    syncMode() {
      if (!this.media.matches) this.slider.scrollLeft = 0;
      this.queueUpdate();
    }

    queueUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      if (!this.media.matches) {
        this.viewport.dataset.reach = 'all';
        this.previous?.removeAttribute('disabled');
        this.next?.removeAttribute('disabled');
        return;
      }
      const max = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      const left = Math.max(0, this.slider.scrollLeft);
      const overflow = max > 2;
      let reach = 'all';
      if (overflow && left <= 2) reach = 'begin';
      else if (overflow && left >= max - 2) reach = 'end';
      else if (overflow) reach = 'progress';
      this.viewport.dataset.reach = reach;
      if (this.previous) this.previous.disabled = !overflow || reach === 'begin';
      if (this.next) this.next.disabled = !overflow || reach === 'end';
    }

    revealBlock(block) {
      const slide = block.closest('[data-category-pill-slide]');
      if (!slide) return;
      if (this.media.matches) slide.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      else this.viewport.scrollTo({ left: slide.offsetLeft - 16, behavior: 'auto' });
      this.queueUpdate();
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.abort.abort();
      instances.delete(this.section);
    }
  }

  const init = scope => scope.querySelectorAll(selector).forEach(section => new CategoryPills(section));
  const getInstance = target => {
    const section = target.closest(selector);
    return section ? instances.get(section) : null;
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(section => instances.get(section)?.destroy()));
  document.addEventListener('shopify:block:select', event => getInstance(event.target)?.revealBlock(event.target));
})();
